/**
 * MetricChart — a compact dark-theme time-series AREA chart for the
 * managed-app Overview "Usage" section. Thin wrapper over Chart.js
 * (`chart.js/auto`, same import pattern as ChartVisualization.jsx):
 * register-free `auto` build, a `useRef` canvas, `new Chart(ctx, …)` on
 * mount/update, `.destroy()` on cleanup so there are no leaked instances.
 *
 * Props:
 *   series   — array of { label, color, points: [{ t:<iso>, v:<num> }] }.
 *              One filled line per entry. Empty / all-empty → renders an
 *              empty grid with a subtle "Collecting data…" overlay.
 *   yMax     — optional fixed y-axis max (e.g. 100 for percentages).
 *   yFmt     — fn(value) → string for y-axis tick + tooltip labels.
 *   showLegend — show the per-series legend (multi-series charts).
 *   live     — when true, a glowing dot pulses on the LEADING (newest) point
 *              of the line, marking the live edge of the data. No dot in the
 *              card header — the pulse rides the line itself.
 *
 * The chart is intentionally chrome-light: no title (the card header owns
 * that), small fonts, muted grid lines, points hidden (line-only) except the
 * live endpoint dot drawn by the plugin below.
 *
 * Live updates: the Chart.js instance is created ONCE (on mount) and then
 * mutated in-place on each `series`/`yMax` change — `dataset.data = …;
 * chart.update()` — so the line tweens smoothly to the new samples instead
 * of flashing through a destroy+recreate. The parent polls metrics (~20s)
 * and passes fresh arrays; this makes it feel like a live monitor.
 */
import React, { useRef, useEffect } from 'react';
import { Chart } from 'chart.js/auto';

const GRID = 'rgba(255, 255, 255, 0.06)';
const TICK = 'rgba(255, 255, 255, 0.45)';

// ── Shared pulse clock ──────────────────────────────────────────────────
// ONE requestAnimationFrame loop drives the live endpoint dot on every chart
// at once (a single rAF for the whole page, not one per card). It runs only
// while ≥1 chart is live + on screen; it advances a 0→1 phase each frame and
// asks each registered chart to repaint (the plugin reads `pulsePhase`).
const liveCharts = new Set();
let pulsePhase = 0;
let rafId = null;
let lastTs = 0;
const PULSE_MS = 1600; // one breathe cycle

function tick(ts) {
  if (!lastTs) lastTs = ts;
  pulsePhase = (pulsePhase + (ts - lastTs) / PULSE_MS) % 1;
  lastTs = ts;
  liveCharts.forEach((c) => {
    // c.draw() repaints from cached geometry (cheap for these tiny charts)
    // and re-runs the plugin so the halo animates. Guard against teardown.
    try { c.draw(); } catch (e) { /* chart mid-destroy */ }
  });
  if (liveCharts.size) {
    rafId = requestAnimationFrame(tick);
  } else {
    rafId = null;
    lastTs = 0;
  }
}

function ensureClock() {
  if (rafId == null && liveCharts.size) {
    lastTs = 0;
    rafId = requestAnimationFrame(tick);
  }
}

// Draws the pulsing "live" dot on the newest point of the first series.
const liveDotPlugin = {
  id: 'zibbyLiveDot',
  afterDatasetsDraw(chart) {
    if (!chart.$live) return;
    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || meta.data.length === 0) return;
    const pt = meta.data[meta.data.length - 1]; // newest sample = line head
    if (!pt || typeof pt.x !== 'number' || typeof pt.y !== 'number') return;
    const color = chart.data.datasets[0]?.borderColor || '#34d399';
    const { ctx } = chart;
    const p = pulsePhase; // 0→1 breathe

    ctx.save();
    // Expanding, fading halo ring.
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 3 + p * 7, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.35 * (1 - p);
    ctx.fill();
    // Solid glowing core.
    ctx.globalAlpha = 1;
    ctx.shadowColor = color;
    ctx.shadowBlur = 8;
    ctx.beginPath();
    ctx.arc(pt.x, pt.y, 3, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.restore();
  },
};

function hasData(series) {
  return Array.isArray(series) && series.some((s) => Array.isArray(s.points) && s.points.length > 0);
}

// Map the `series` prop → Chart.js datasets. Needs the 2d ctx so each
// series gets its own vertical gradient fill (DigitalOcean/Grafana look).
function buildDatasets(ctx, series) {
  return (series || []).map((s) => {
    const grad = ctx.createLinearGradient(0, 0, 0, 160);
    grad.addColorStop(0, `${s.color}59`); // ~35% at the top
    grad.addColorStop(1, `${s.color}00`); // transparent at the bottom
    return {
      label: s.label,
      data: (s.points || []).map((p) => ({ x: new Date(p.t).valueOf(), y: p.v })),
      borderColor: s.color,
      backgroundColor: grad,
      borderWidth: 1.5,
      fill: true,
      tension: 0.35,
      pointRadius: 0,
      pointHoverRadius: 3,
      pointHoverBackgroundColor: s.color,
    };
  });
}

const MetricChart = ({ series, yMax, yFmt, showLegend = false, live = false }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const fmt = yFmt || ((v) => `${v}`);
  const populated = hasData(series);

  // ── Create the chart ONCE (mount). Data is mutated in-place below so
  // polled refreshes tween smoothly instead of flashing on recreate.
  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const ctx = canvasRef.current.getContext('2d');

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: { datasets: buildDatasets(ctx, series) },
      plugins: [liveDotPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        // Smooth ~500ms ease on .update() so the line tweens to fresh
        // samples — the "live monitoring" feel. First paint is instant.
        animation: { duration: 500, easing: 'easeOutQuart' },
        // Leave room so the endpoint dot's halo isn't clipped at the edges.
        layout: { padding: { right: 6, top: 6 } },
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            type: 'linear',
            display: false,
            grid: { display: false },
          },
          y: {
            beginAtZero: true,
            ...(typeof yMax === 'number' ? { max: yMax, suggestedMax: yMax } : {}),
            grid: { color: GRID, drawTicks: false },
            border: { display: false },
            ticks: {
              color: TICK,
              font: { size: 10 },
              maxTicksLimit: 4,
              padding: 6,
              callback: (value) => fmt(value),
            },
          },
        },
        plugins: {
          legend: showLegend
            ? {
                display: true,
                position: 'bottom',
                align: 'start',
                labels: {
                  color: TICK,
                  boxWidth: 8,
                  boxHeight: 8,
                  usePointStyle: true,
                  pointStyle: 'circle',
                  font: { size: 10 },
                  padding: 10,
                },
              }
            : { display: false },
          tooltip: {
            enabled: true,
            displayColors: true,
            // Circular color indicator (not the default square) so the swatch
            // reads as a dot matching the line.
            usePointStyle: true,
            callbacks: {
              title: (items) => {
                if (!items.length) return '';
                const d = new Date(items[0].parsed.x);
                return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              },
              label: (item) => `${item.dataset.label}: ${fmt(item.parsed.y)}`,
              // Solid line color (the dataset's gradient fill would render a
              // washed-out / near-white box) drawn as a filled circle.
              labelColor: (item) => ({
                borderColor: item.dataset.borderColor,
                backgroundColor: item.dataset.borderColor,
                borderWidth: 0,
                borderRadius: 6,
              }),
              labelPointStyle: () => ({ pointStyle: 'circle', rotation: 0 }),
            },
          },
        },
      },
    });

    const inst = chartRef.current;
    return () => {
      liveCharts.delete(inst);
      inst.destroy();
      if (chartRef.current === inst) chartRef.current = null;
    };
    // Structural config only (axis max, formatter, legend). These are static
    // per card, so in practice the chart is built exactly once and survives
    // every poll. `series`/`live` are intentionally omitted — handled by the
    // effects below (in-place data sync + live-dot registration).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yMax, fmt, showLegend]);

  // ── Sync data in-place on every `series` change (the poll path). Mutate
  // the existing datasets and call .update() so Chart.js tweens the line to
  // the new points (configured ~500ms ease above). Handles empty→data and
  // data→empty too: buildDatasets just yields empty `data` arrays, which the
  // "Collecting data…" overlay covers.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const ctx = canvasRef.current.getContext('2d');
    chart.data.datasets = buildDatasets(ctx, series);
    chart.update();
  }, [series]);

  // ── Register/unregister this chart with the shared pulse clock. The dot
  // only animates while the app is live AND there's at least one sample to
  // anchor it to. Flipping off redraws once to clear the dot.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return undefined;
    const on = !!live && populated;
    chart.$live = on;
    if (on) {
      liveCharts.add(chart);
      ensureClock();
    } else {
      liveCharts.delete(chart);
      try { chart.draw(); } catch (e) { /* mid-teardown */ }
    }
    return () => { liveCharts.delete(chart); };
  }, [live, populated]);

  return (
    <div className="metric-chart">
      <canvas ref={canvasRef} />
      {!populated && (
        <div className="metric-chart-empty">Collecting data…</div>
      )}
    </div>
  );
};

export default MetricChart;
